// The resolution engine. Job inputs + a library snapshot → BOM → route → minutes → dollars,
// deterministically. Same inputs and same standards version give the same number, to the
// cent, every time; nothing here calls a database, a network or an AI.
//
// How one job resolves:
//   1. Standards: company default → the job's value → a Build's override.
//   2. Items: a parent creates its children (a box makes its doors) with inputs mapped from it.
//   3. Names resolve: the item's override → its input → its derived value → the group's
//      derived value → the job standard → the catalog default.
//   4. Items of the same catalog item and recipe form a group. A group-scope line is worked
//      out once for the group and shared back to its items in proportion to what each would
//      need on its own. The recipe is the one the job's standard names for that kind of item
//      (box_build: Shop-built or Cabinotch), or the item's own pick, or the default.
//   5. Pooling (Spencer, 2026-10-06): sheets and setups are independent of which items are
//      drawn in. Every operation's setup is paid once per pool, on the first step that uses
//      it. A formula reads the whole pool with pool_sum / pool_count / pool_count_if, so one
//      material's sheets round up once across every kind of cabinet, and each group takes
//      its share. The pool scope is a setting: the whole job, or each room. A quantity typed
//      on the job route is the exception (Spencer, 2026-10-12): it is priced exactly as typed.
//   8. Rooms (Spencer, 2026-10-06): a job may have rooms. A room follows the job's standards
//      or holds its own values for some of them; a Build's override wins over both. An item
//      is in one room (a child in its parent's), and items of the same kind in different
//      rooms are different groups, so a room's own standard never leaks across.
//   6. Price = (labor × (1 + contingency) + Σ in-margin lines cost × (1 + the line's markup) + their sales tax)
//      / (1 − margin) + Σ outside lines cost × (1 + the line's markup) + their tax, rounded to the cent once for
//      the job; group and item prices are shares that add up to it exactly, so how the items are grouped never
//      moves the total by a cent. Every purchased line pays the company sales tax on its cost (Spencer,
//      2026-10-22). A line's markup is the company materials markup unless the line carries Separate pricing with
//      its own; Separate pricing with Ignore margin moves the line outside the margin (what large purchase did).
//      A material line may carry a typed price (a Cabinotch box, as quoted) in place of a material's price.
//   7. thickness_<role> reads the real thickness of the material filling a role: its nominal
//      thickness less its undersize, both set on the material.

import { Rat, allocateCents } from './rational.ts';
import { calc, identifiers, isBlank, toRat, truthy, type Scope, type Value } from './expr.ts';
import { parseLength } from './units.ts';
import { materialPack, packsFor } from './packs.ts';
import type {
  BomLink, BomPack, JobIn, JobItemIn, LibCatalogItem, LibChild, LibMaterial, LibOperation, LibPart, LibPricing, LibRecipe,
  LibStandardDef, Library, RecipeAvailability, Resolution, ResolveOptions, ResolvedBom, ResolvedGroup, ResolvedInput,
  ResolvedItem, ResolvedPart, ResolvedStandardUse, ResolvedStep, Scalar, Share, SubLineOff, Trace, UnverifiedLine, UnverifiedStep,
} from './types.ts';

const HUNDRED = Rat.of(100n);
const SIXTY = Rat.of(60n);
const MAX_DEPTH = 5;

type ValueType = 'number' | 'text' | 'boolean' | 'choice' | 'material' | 'catalog_item' | 'recipe' | 'auto';

function convert(s: Scalar | undefined, type: ValueType): Value {
  if (s === undefined || s === null) return null;
  if (typeof s === 'boolean') return s;
  if (typeof s === 'number') return Rat.parse(s);
  const t = s.trim();
  if (t === '') return null;
  if (type === 'boolean') return /^(true|yes|1)$/i.test(t);
  if (type === 'number' || type === 'auto') {
    const r = parseLength(t);
    if (r) return r;
  }
  return t;
}

function scalar(v: Value): Scalar {
  return v instanceof Rat ? v.toDecimal(6) : v;
}

function message(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** A scope over a map of standards, for a rule read over the job's standards alone. */
function stdScope(std: Map<string, Value>): Scope {
  return { get: (name) => std.get(name) };
}

/** The rule that makes a standard's chosen value unavailable under these standards, or null. A broken rule reads as available. */
function unavailableChoice(def: LibStandardDef, value: Value | undefined, std: Map<string, Value>): string | null {
  if (!def.choices || value === undefined || value === null) return null;
  const choice = def.choices.find((c) => c.value === String(scalar(value)));
  const expr = choice?.unavailable_expr?.trim();
  if (!expr) return null;
  try {
    return truthy(calc(expr, stdScope(std))) ? expr : null;
  } catch {
    return null;
  }
}

/**
 * For the screens: which choices of a standard are unavailable under a job's standards (as the resolution reports
 * them), each with its rule (Spencer's change log, 2026-10-07: white melamine only through Cabinotch). A choice with
 * no rule, or a rule that cannot be read, is available.
 */
export function unavailableChoices(lib: Library, standards: Record<string, Scalar>, def: LibStandardDef): Record<string, string> {
  const std = new Map<string, Value>();
  for (const d of lib.standards) std.set(d.key, convert(standards[d.key] ?? d.default_value, d.data_type));
  const out: Record<string, string> = {};
  for (const c of def.choices ?? []) {
    const expr = c.unavailable_expr?.trim();
    if (!expr) continue;
    try {
      if (truthy(calc(expr, stdScope(std)))) out[c.value] = expr;
    } catch {
      /* a broken rule reads as available; the Standards editor keeps bad formulas out */
    }
  }
  return out;
}

interface WorkItem {
  id: string;
  stored: boolean;
  number: string;
  name: string;
  cat: LibCatalogItem | null;
  recipe: LibRecipe | null;
  /** The room the item is in; a child's is its parent's. */
  room_id: string | null;
  parent: WorkItem | null;
  viaChild: LibChild | null;
  childIndex: number | null;
  inputs: Record<string, Scalar>;
  overrides: Record<string, Scalar>;
  row: JobItemIn | null;
  isCustom: boolean;
  warnings: string[];
  note: string | null;
  sort: number;
}

interface Env {
  lib: Library;
  /** The job's standards in effect (company default, the job's value, a Build's override). */
  std: Map<string, Value>;
  stdDefs: Map<string, LibStandardDef>;
  /** The standards in effect for an item in a room: the room's own values, when it holds them, under the Build's override. */
  stdFor: (roomId: string | null) => Map<string, Value>;
  /** Every item on the job, in order. */
  pool: ItemCtx[];
  /** The pool an item in a room reads and pays setup within: the whole job, or its room, as the pooling setting says. */
  poolFor: (roomId: string | null) => ItemCtx[];
}

/** The items of one kind on the job: what they share (the room's standards, the sheet filling a role). No formula runs here any more: every formula runs per item. */
class GroupCtx implements Scope {
  items: ItemCtx[] = [];

  constructor(readonly cat: LibCatalogItem | null, readonly env: Env, readonly roomId: string | null = null) {}

  /** The standards this group's items read: those of their room. */
  get std(): Map<string, Value> {
    return this.env.stdFor(this.roomId);
  }

  get(name: string): Value | undefined {
    // an input that follows a standard: the group's items all hold the same value (it is part of the group key), so the
    // first item's reading, override and all, is the group's (Spencer, 2026-10-08: a box's own carcass material reaches its sheets)
    const first0 = this.items[0];
    if (first0 && first0.followed(name)) return first0.get(name);
    const std = this.std.get(name);
    if (std !== undefined) return std;
    // thickness_<role> and sheet_area_<role> read the material filling the role on this job: the same for every item of the group
    const first = this.items[0];
    if (first) {
      const t = first.thicknessOf(name);
      if (t !== undefined) return t;
      const a = first.sheetAreaOf(name);
      if (a !== undefined) return a;
    }
    return undefined;
  }

  members(): Scope[] {
    return this.env.poolFor(this.roomId);
  }

  pool(): Scope[] {
    return this.env.poolFor(this.roomId);
  }
}

/** One part of one item, worked out. */
interface PartCalc {
  part: LibPart;
  tally: string;
  qty: Rat;
  length: Rat;
  width: Rat;
  bandLong: Rat;
  bandShort: Rat;
  area: Rat;
  band: Rat;
}

/** The name a part's totals go by in formulas: the role it is cut from (area_carcass_3_4), or "parts" for a fixed material. */
const tallyOf = (p: LibPart) => (p.material_role?.trim() || 'parts');

class ItemCtx implements Scope {
  group: GroupCtx | null = null;
  private memo = new Map<string, Value>();
  private busy = new Set<string>();
  private partsMemo: PartCalc[] | null = null;
  private partsBusy = false;
  private mapped: Map<string, Value> | null = null;
  readonly traces = new Map<string, Trace[]>();

  constructor(readonly w: WorkItem, readonly env: Env, readonly parentCtx: ItemCtx | null) {}

  /** The standards this item reads: the job's, or its room's own where the room holds them. */
  get std(): Map<string, Value> {
    return this.env.stdFor(this.w.room_id);
  }

  typeOf(key: string): ValueType {
    const inp = this.w.cat?.inputs.find((i) => i.key === key);
    if (inp) {
      // an input that follows a standard is typed as the standard is
      if (inp.data_type === 'standard') return this.env.stdDefs.get(inp.standard_key?.trim() || key)?.data_type ?? 'auto';
      return inp.data_type;
    }
    const def = this.env.stdDefs.get(key);
    if (def) return def.data_type;
    return 'auto';
  }

  /** A child's inputs, worked out from its parent. The child's own overrides are seen first. */
  mappedInputs(): Map<string, Value> {
    if (this.mapped) return this.mapped;
    const out = new Map<string, Value>();
    this.mapped = out;
    const child = this.w.viaChild;
    const parent = this.parentCtx;
    if (!child || !parent) return out;
    const self = this;
    const scope: Scope = {
      get(name) {
        if (name === 'child_index') return Rat.of(BigInt(self.w.childIndex ?? 0));
        const o = self.w.overrides[name];
        if (o !== undefined && o !== null && o !== '') return convert(o, self.typeOf(name));
        return parent.get(name);
      },
      members: () => parent.members(),
    };
    for (const [key, expr] of Object.entries(child.input_map)) {
      try {
        const trace: Trace[] = [];
        out.set(key, calc(expr, scope, trace));
        this.traces.set(key, trace);
      } catch (e) {
        this.w.warnings.push(`${key}: ${message(e)}`);
        out.set(key, null);
      }
    }
    return out;
  }

  /**
   * The item's parts, each with how many, how long and how wide. A part whose count comes
   * to zero is left out. Worked out once per item.
   */
  partCalcs(): PartCalc[] {
    if (this.partsMemo) return this.partsMemo;
    const parts = this.w.recipe?.parts ?? [];
    if (parts.length === 0) return (this.partsMemo = []);
    if (this.partsBusy) throw new Error('a part\'s size refers back to the parts totals');
    this.partsBusy = true;
    try {
      const out: PartCalc[] = [];
      for (const p of parts) {
        const num = (expr: string | null, what: string, blank: Rat): Rat => {
          if (!expr || expr.trim() === '') return blank;
          try {
            const v = calc(expr, this);
            return isBlank(v) ? blank : toRat(v);
          } catch (e) {
            this.w.warnings.push(`Part "${p.name}", ${what}: ${message(e)}`);
            return Rat.ZERO;
          }
        };
        const qty = num(p.qty_expr, 'how many', Rat.ONE);
        if (qty.sign() <= 0) continue;
        const length = num(p.length_expr, 'length', Rat.ZERO);
        const width = num(p.width_expr, 'width', Rat.ZERO);
        if (length.sign() <= 0 || width.sign() <= 0) this.w.warnings.push(`Part "${p.name}" has no size yet: fill in the fields its length and width are worked out from.`);
        const bandLong = num(p.band_long_expr, 'banded long edges', Rat.ZERO);
        const bandShort = num(p.band_short_expr, 'banded short edges', Rat.ZERO);
        const L = length.sign() > 0 ? length : Rat.ZERO;
        const W = width.sign() > 0 ? width : Rat.ZERO;
        out.push({ part: p, tally: tallyOf(p), qty, length: L, width: W, bandLong, bandShort, area: qty.mul(L).mul(W), band: qty.mul(bandLong.mul(L).add(bandShort.mul(W))) });
      }
      return (this.partsMemo = out);
    } finally {
      this.partsBusy = false;
    }
  }

  /**
   * What the item's other formulas can read from its parts: area_<tally> (square inches),
   * count_<tally> and band_<tally> (inches of banded edge) for each tally its parts name, and
   * parts_area, parts_count and parts_band across all of them.
   */
  partValue(name: string): Value | undefined {
    // a tally any recipe of this item names is readable under every recipe: a bought recipe
    // has no parts, so its areas, counts and banding come to zero instead of an unknown name
    const named = this.w.cat?.recipes.flatMap((r) => r.parts) ?? [];
    if (named.length === 0) return undefined;
    const whole = name === 'parts_area' || name === 'parts_count' || name === 'parts_band';
    const m = whole ? null : /^(area|count|band)_(.+)$/.exec(name);
    if (!whole && (!m || !named.some((p) => tallyOf(p) === m[2]))) return undefined;
    const kind = whole ? name.slice(6) : m![1]!;
    let total = Rat.ZERO;
    for (const c of this.partCalcs()) {
      if (!whole && c.tally !== m![2]) continue;
      total = total.add(kind === 'area' ? c.area : kind === 'count' ? c.qty : c.band);
    }
    return total;
  }

  /** The standard an input follows, when the catalog input of that key is a standard input (Spencer, 2026-10-07: hinge placement). */
  followed(key: string): LibStandardDef | null {
    const inp = this.w.cat?.inputs.find((i) => i.key === key);
    if (!inp || inp.data_type !== 'standard') return null;
    return this.env.stdDefs.get(inp.standard_key?.trim() || key) ?? null;
  }

  /** Whether this item may hold its own value of a name: always, except for an input following a standard that allows none. */
  overridable(key: string): boolean {
    const def = this.followed(key);
    return !def || def.allow_item_override !== false;
  }

  sourceOf(key: string): ResolvedInput['source'] {
    const o = this.w.overrides[key];
    if (o !== undefined && o !== null && o !== '' && this.overridable(key)) return 'override';
    if (this.mappedInputs().has(key)) return 'parent';
    const followed = this.followed(key);
    if (followed) {
      // a catalog input (Spencer, 2026-10-14) is typed per item and starts at the preset's default
      if (followed.kind === 'catalog') {
        const typed = this.w.inputs[key];
        if (typed !== undefined && typed !== null && typed !== '') return 'input';
        return followed.default_value !== null && followed.default_value !== undefined && followed.default_value !== '' ? 'default' : 'blank';
      }
      const v = this.std.get(followed.key);
      return v !== undefined && !isBlank(v) ? 'standard' : 'blank';
    }
    const i = this.w.inputs[key];
    if (i !== undefined && i !== null && i !== '') return 'input';
    if (this.std.has(key) && !isBlank(this.std.get(key)!)) return 'standard';
    const def = this.w.cat?.inputs.find((x) => x.key === key);
    if (def && def.default_value !== null && def.default_value !== '') return 'default';
    return 'blank';
  }

  get(name: string): Value | undefined {
    const w = this.w;
    const o = w.overrides[name];
    if (o !== undefined && o !== null && o !== '' && this.overridable(name)) return convert(o, this.typeOf(name));
    const mapped = this.mappedInputs();
    if (mapped.has(name)) return mapped.get(name)!;
    // an input that follows a standard reads the standard (the room's own where it holds one), never a value typed on the item;
    // one that follows a catalog input reads what is typed on the item, else the preset's default (Spencer, 2026-10-14)
    const followed = this.followed(name);
    if (followed) {
      if (followed.kind === 'catalog') {
        const typed = w.inputs[name];
        if (typed !== undefined && typed !== null && typed !== '') return convert(typed, followed.data_type);
        return convert(followed.default_value, followed.data_type);
      }
      return this.std.get(followed.key) ?? null;
    }
    const i = w.inputs[name];
    if (i !== undefined && i !== null && i !== '') return convert(i, this.typeOf(name));

    const d = w.cat?.derived.find((x) => x.key === name);
    if (d) {
      if (this.memo.has(name)) return this.memo.get(name)!;
      if (this.busy.has(name)) throw new Error(`"${name}" refers back to itself`);
      this.busy.add(name);
      try {
        const trace: Trace[] = [];
        const v = calc(d.expr, this, trace);
        this.memo.set(name, v);
        this.traces.set(name, trace);
        return v;
      } finally {
        this.busy.delete(name);
      }
    }
    const fromParts = this.partValue(name);
    if (fromParts !== undefined) return fromParts;
    // the recipe this item resolved through, so an order item can count the job's Cabinotch boxes
    if (name === 'recipe_name') return w.recipe?.name ?? null;
    // what the item is, so an order item can count the job's cabinet boxes (Spencer, 2026-10-15: the finisher paints the boxes too)
    if (name === 'catalog_family') return w.cat?.family ?? null;
    if (name === 'catalog_item') return w.cat?.name ?? null;
    if (this.std.has(name)) return this.std.get(name)!;
    const def = w.cat?.inputs.find((x) => x.key === name);
    if (def) return convert(def.default_value, this.typeOf(name));
    const thickness = this.thicknessOf(name);
    if (thickness !== undefined) return thickness;
    const sheetArea = this.sheetAreaOf(name);
    if (sheetArea !== undefined) return sheetArea;
    return undefined;
  }

  /**
   * sheet_area_<role>: the square inches of one sheet of the material filling that role on
   * this job, its length times its width, both set on the material (96 × 48 for a 4 x 8).
   * The sheet size is not a standard (Spencer, 2026-10-06): it depends on the plywood chosen.
   * A material with no size set stops the formula and says so.
   */
  sheetAreaOf(name: string): Value | undefined {
    const m = /^sheet_area_(.+)$/.exec(name);
    if (!m) return undefined;
    const role = m[1]!;
    const mat = materialForRole(role, this, this.env);
    if (!mat) throw new Error(`no material chosen for "${role}" on this job, so its sheet size is not known`);
    const blank = (v: string | null | undefined) => v === null || v === undefined || v === '';
    if (blank(mat.sheet_length_in) || blank(mat.sheet_width_in)) {
      throw new Error(`${mat.description} has no sheet size set in the library (length and width, under Materials)`);
    }
    return Rat.parse(mat.sheet_length_in!).mul(Rat.parse(mat.sheet_width_in!));
  }

  /**
   * thickness_<role>: the real thickness of the sheet filling that role on this job, its
   * nominal thickness less its undersize (3/4 Premcore Plus is 1/32 under). Both live on the
   * material. A material with no thickness set stops the formula; one with no undersize set
   * is taken at its nominal thickness and flagged.
   */
  thicknessOf(name: string): Value | undefined {
    const m = /^thickness_(.+)$/.exec(name);
    if (!m) return undefined;
    const role = m[1]!;
    const mat = materialForRole(role, this, this.env);
    if (!mat) throw new Error(`no material chosen for "${role}" on this job, so its thickness is not known`);
    if (mat.nominal_thickness_in === null || mat.nominal_thickness_in === undefined || mat.nominal_thickness_in === '') {
      throw new Error(`${mat.description} has no thickness set in the library`);
    }
    const nominal = Rat.parse(mat.nominal_thickness_in);
    if (mat.undersize_in === null || mat.undersize_in === undefined || mat.undersize_in === '') {
      this.w.warnings.push(`${mat.description} has no undersize set in the library, so it is taken at a full ${mat.nominal_thickness_in} in.`);
      return nominal;
    }
    return nominal.sub(Rat.parse(mat.undersize_in));
  }

  /** sum(), count() and count_if() read the whole pool: every item on the job, or in the room. */
  members(): Scope[] {
    return this.env.poolFor(this.w.room_id);
  }

  pool(): Scope[] {
    return this.env.poolFor(this.w.room_id);
  }
}

function pct(v: number | string): Rat {
  return Rat.parse(v).div(HUNDRED);
}

function materialForRole(role: string, scope: Scope, env: Env): LibMaterial | null {
  const direct = scope.get(role);
  if (typeof direct === 'string' && env.lib.materials[direct]) return env.lib.materials[direct]!;
  for (const def of env.lib.standards) {
    if (!def.choices) continue;
    const chosen = scope.get(def.key);
    const choice = def.choices.find((c) => c.value === chosen);
    const id = choice?.materials?.[role];
    if (id && env.lib.materials[id]) return env.lib.materials[id]!;
  }
  return null;
}

export function resolveJob(lib: Library, job: JobIn, opts: ResolveOptions = {}): Resolution {
  const warnings: string[] = [];

  // 1. standards in effect
  const std = new Map<string, Value>();
  const stdDefs = new Map<string, LibStandardDef>();
  const jobStd = new Map<string, Value>();
  for (const def of lib.standards) {
    stdDefs.set(def.key, def);
    // a catalog input is not a job standard: it is read only through the input that pulls it in
    if (def.kind === 'catalog') continue;
    const jobValue = job.standards[def.key];
    const base = jobValue !== undefined && jobValue !== null && jobValue !== '' ? jobValue : def.default_value;
    jobStd.set(def.key, convert(base, def.data_type));
    const over = opts.standardOverrides?.[def.key];
    std.set(def.key, convert(over !== undefined && over !== null && over !== '' ? over : base, def.data_type));
  }
  for (const [k, v] of Object.entries(job.standards)) {
    // a value a job holds for a catalog input is not read: that input is typed on the item
    if (!std.has(k) && stdDefs.get(k)?.kind !== 'catalog') {
      std.set(k, convert(v, 'auto'));
      jobStd.set(k, convert(v, 'auto'));
    }
  }
  // a room that holds its own standards: its values over the job's, under a Build's override
  const roomStd = new Map<string, Map<string, Value>>();
  for (const room of job.rooms ?? []) {
    if (!room.own_standards) continue;
    const m = new Map(std);
    for (const [k, v] of Object.entries(room.standards)) {
      if (v === undefined || v === null || v === '') continue;
      const over = opts.standardOverrides?.[k];
      if (over !== undefined && over !== null && over !== '') continue; // the Build wins
      m.set(k, convert(v, stdDefs.get(k)?.data_type ?? 'auto'));
    }
    roomStd.set(room.id, m);
  }
  // a choice a job standard holds may be unavailable on this job by its own rule (TFL White only through Cabinotch)
  for (const def of lib.standards) {
    const reason = unavailableChoice(def, std.get(def.key), std);
    if (reason) warnings.push(`${def.label}: "${scalar(std.get(def.key) ?? null)}" is not available on this job (${reason}). Pick another in Job setup.`);
  }
  const poolScope = String(lib.pooling?.scope ?? 'job').trim().toLowerCase();
  if (poolScope !== 'job' && poolScope !== 'room') warnings.push(`The pooling setting asks for "${lib.pooling?.scope}", which is not built; sheets and setups are pooled across the job (set it to "job" or "room").`);
  const byRoom = poolScope === 'room';
  const roomPools = new Map<string | null, ItemCtx[]>();
  const env: Env = {
    lib,
    std,
    stdDefs,
    stdFor: (roomId) => (roomId ? roomStd.get(roomId) ?? std : std),
    pool: [],
    poolFor: (roomId) => (byRoom ? roomPools.get(roomId ?? null) ?? [] : env.pool),
  };

  // 2. items, with the children their parents create. An item in an add-on is priced only when that add-on is on; an
  // alternate in an add-on that is on stands in for the base item it replaces; a child follows its parent (2026-10-17).
  const addOnsOn = new Set(opts.addOns ?? []);
  const inPrice = (r: JobItemIn) => !r.add_on_id || addOnsOn.has(r.add_on_id);
  const replacedIds = new Set(job.items.filter((r) => r.replaces_item_id && inPrice(r)).map((r) => r.replaces_item_id!));
  const everyRow = new Map(job.items.map((r) => [r.id, r]));
  const priced = (r: JobItemIn): boolean => {
    let x: JobItemIn | undefined = r;
    for (let d = 0; x && d <= MAX_DEPTH; d++) {
      if (!inPrice(x) || replacedIds.has(x.id)) return false;
      x = x.parent_item_id ? everyRow.get(x.parent_item_id) : undefined;
    }
    return true;
  };
  const rows = job.items.filter(priced).sort((a, b) => a.sort - b.sort || a.number.localeCompare(b.number));
  const byId = new Map(rows.map((r) => [r.id, r]));
  // A stored child is found by the link that creates it and its position in that link. Rows
  // stored before links were recorded carry no link; they are found by catalog item instead,
  // and go to the first link that creates that item.
  const storedByLink = new Map<string, JobItemIn>();
  const storedByItem = new Map<string, JobItemIn>();
  for (const r of rows) {
    if (!r.parent_item_id || r.child_index === null) continue;
    if (r.child_link_id) storedByLink.set(`${r.parent_item_id}|${r.child_link_id}|${r.child_index}`, r);
    else if (r.catalog_item_id) storedByItem.set(`${r.parent_item_id}|${r.catalog_item_id}|${r.child_index}`, r);
  }
  const used = new Set<string>();
  const work: WorkItem[] = [];
  const ctxOf = new Map<string, ItemCtx>();

  const defaultRecipe = (cat: LibCatalogItem): LibRecipe | null => cat.recipes.find((r) => r.is_default) ?? cat.recipes[0] ?? null;
  const byName = (cat: LibCatalogItem, name: string): LibRecipe | undefined => cat.recipes.find((r) => r.name.trim().toLowerCase() === name.trim().toLowerCase());

  /**
   * Which recipe an item resolves through: the one picked on the item itself (an Add-on's swap,
   * or a stored pick), else the one the job standard the catalog item names holds (box_build:
   * "Cabinotch"), read through the item so a Build's override and an item's own override both
   * count, else the default. An item with no recipe of the chosen name is flagged, not priced.
   */
  const chooseRecipe = (w: WorkItem, ctx: ItemCtx): LibRecipe | null => {
    const cat = w.cat;
    if (!cat) return null;
    // a stored pick belongs to the row's own catalog item: it does not carry to one an Add-on swaps in
    const patch = opts.itemPatches?.[w.id];
    const picked = patch?.recipe_id ?? (patch?.catalog_item_id ? null : w.row?.recipe_id) ?? null;
    if (picked) {
      const r = cat.recipes.find((x) => x.id === picked);
      if (r) return r;
      w.warnings.push('The recipe picked for this item is no longer on its catalog item; its default is used.');
    }
    const stdKey = cat.recipe_standard?.trim();
    if (stdKey) {
      let v: Value | undefined;
      try {
        v = ctx.get(stdKey);
      } catch (e) {
        w.warnings.push(`${stdKey}: ${message(e)}`);
      }
      const name = typeof v === 'string' ? v.trim() : '';
      if (name) {
        const r = byName(cat, name);
        if (r) return r;
        w.warnings.push(`No recipe named "${name}" on ${cat.name}, so it is not priced. Add one in the Library, or pick another recipe for this item.`);
        return null;
      }
      const def = stdDefs.get(stdKey);
      if (!def) w.warnings.push(`"${stdKey}" is not a standard, so the recipe falls back to the default.`);
    }
    return defaultRecipe(cat);
  };

  const make = (row: JobItemIn | null, base: Partial<WorkItem> & { id: string; number: string; name: string }, parent: WorkItem | null): WorkItem => {
    const patch = opts.itemPatches?.[base.id];
    // an Add-on may swap a stored item to another catalog item; a child the parent creates is never swapped
    const swapped = row && patch?.catalog_item_id ? patch.catalog_item_id : null;
    const catId = swapped ?? row?.catalog_item_id ?? base.cat?.id ?? null;
    const cat = swapped ? lib.catalog[swapped] ?? null : base.cat ?? (catId ? lib.catalog[catId] ?? null : null);
    const w: WorkItem = {
      id: base.id,
      stored: row !== null,
      number: base.number,
      name: base.name,
      cat,
      recipe: null, // chosen once the item has a scope to read the job's standards through
      // a child is in its parent's room; a top-level item in the room its row names
      room_id: parent ? parent.room_id : row?.room_id ?? null,
      parent,
      viaChild: base.viaChild ?? null,
      childIndex: base.childIndex ?? null,
      inputs: { ...(row?.inputs ?? {}), ...(patch?.inputs ?? {}) },
      overrides: { ...(row?.overrides ?? {}), ...(patch?.overrides ?? {}) },
      row,
      // an item swapped to another catalog item is that item, whatever route its row froze
      isCustom: swapped ? false : row?.is_custom ?? false,
      warnings: [],
      note: row?.note ?? null,
      sort: row?.sort ?? 0,
    };
    if (row && catId && !cat) w.warnings.push(swapped ? 'The catalog item the add-on swaps in is no longer in the library.' : 'Its catalog item is no longer in the library.');
    return w;
  };

  const expand = (w: WorkItem, depth: number) => {
    work.push(w);
    used.add(w.id);
    const parentCtx = w.parent ? ctxOf.get(w.parent.id) ?? null : null;
    const ctx = new ItemCtx(w, env, parentCtx);
    ctxOf.set(w.id, ctx);
    env.pool.push(ctx);
    if (!roomPools.has(w.room_id)) roomPools.set(w.room_id, []);
    roomPools.get(w.room_id)!.push(ctx);
    if (!w.isCustom) w.recipe = chooseRecipe(w, ctx);
    if (!w.cat || depth >= MAX_DEPTH) return;
    // children are numbered straight through, link after link: B-01.1, B-01.2, …
    let made = 0;
    for (const child of w.cat.children) {
      // a fixed catalog item, or the one the job's standards (and this item's overrides) name
      let childCat = child.child_catalog_item_id ? lib.catalog[child.child_catalog_item_id] : undefined;
      if (!childCat && child.child_expr) {
        try {
          const v = calc(child.child_expr, ctx);
          const key = typeof v === 'string' ? v.trim() : '';
          childCat = key ? lib.catalog[key] ?? Object.values(lib.catalog).find((c) => c.name.toLowerCase() === key.toLowerCase()) : undefined;
          if (!childCat) {
            w.warnings.push(key ? `No catalog item named "${key}" for the children it creates.` : `No type chosen for the children it creates (${child.child_expr}), so they are not priced.`);
            continue;
          }
        } catch (e) {
          w.warnings.push(`Children: ${message(e)}`);
          continue;
        }
      }
      if (!childCat) {
        w.warnings.push('A child item it creates is no longer in the library.');
        continue;
      }
      let count = 0;
      try {
        count = Number(toRat(calc(child.count_expr, ctx)).floor());
      } catch (e) {
        w.warnings.push(`Children of ${childCat.name}: ${message(e)}`);
      }
      for (let i = 0; i < Math.max(0, Math.min(count, 200)); i++) {
        const legacy = storedByItem.get(`${w.id}|${childCat.id}|${i}`);
        const row = storedByLink.get(`${w.id}|${child.id}|${i}`) ?? (legacy && !used.has(legacy.id) ? legacy : null);
        made++;
        const c = make(
          row,
          {
            id: row?.id ?? `${w.id}~${child.id}~${i}`,
            number: row?.number || `${w.number}.${made}`,
            name: row?.name || child.label || childCat.name,
            cat: childCat,
            viaChild: child,
            childIndex: i,
          },
          w,
        );
        expand(c, depth + 1);
      }
    }
    // stored rows nested under this item that are not auto-created children
    for (const r of rows) {
      if (r.parent_item_id === w.id && !used.has(r.id) && r.child_index === null) {
        expand(make(r, { id: r.id, number: r.number, name: r.name }, w), depth + 1);
      }
    }
  };

  for (const r of rows) {
    if (used.has(r.id)) continue;
    if (r.parent_item_id && byId.has(r.parent_item_id)) continue; // reached through its parent
    expand(make(r, { id: r.id, number: r.number, name: r.name }, null), 0);
  }
  for (const r of rows) {
    if (!used.has(r.id)) {
      warnings.push(`${r.number || r.name}: its parent no longer creates it, so it is not priced.`);
    }
  }

  // 3. groups: same catalog item, recipe and room; a custom or bare item is a group of its own.
  // A group is only how the lines are shown and shared back to items: every formula runs per
  // item, and what is bought or done whole is rounded once per pool (Spencer, 2026-10-08).
  const groups = new Map<string, { key: string; ctx: GroupCtx; items: ItemCtx[]; recipe: LibRecipe | null; kind: 'items' | 'job'; roomId: string | null }>();
  const groupKeyOf = new Map<string, string>();
  for (const w of work) {
    // items that picked different materials on a material input, or hold a different value of a standard they follow (a box's own carcass
    // material), are different groups, so a line filled by that input prices with each item's own
    const picked = w.cat && !w.isCustom
      ? w.cat.inputs
          .filter((i) => i.data_type === 'material' || (i.data_type === 'standard' && stdDefs.get(i.standard_key?.trim() || i.key)?.kind !== 'catalog'))
          .map((i) => `|${i.key}=${scalar(ctxOf.get(w.id)?.get(i.key) ?? null) ?? ''}`)
          .join('')
      : '';
    // an item that carries its own route or BOM lines beside its recipe (a step added, a recipe step changed or removed on
    // this job item only: 2026-10-18) prices on its own, so the lines land on it and no other item of its kind
    const ownLines = !w.isCustom && w.cat && ((w.row?.custom_route ?? []).length > 0 || (w.row?.custom_bom ?? []).length > 0) ? `|own:${w.id}` : '';
    const key = w.isCustom || !w.cat ? `custom:${w.id}` : `${w.cat.id}:${w.recipe?.id ?? 'none'}${w.room_id ? `:${w.room_id}` : ''}${picked}${ownLines}`;
    groupKeyOf.set(w.id, key);
    let g = groups.get(key);
    if (!g) {
      g = { key, ctx: new GroupCtx(w.isCustom ? null : w.cat, env, w.room_id), items: [], recipe: w.isCustom ? null : w.recipe, kind: 'items', roomId: w.room_id };
      groups.set(key, g);
    }
    const ctx = ctxOf.get(w.id)!;
    ctx.group = g.ctx;
    g.ctx.items.push(ctx);
    g.items.push(ctx);
  }
  // the job route: the job's own steps and lines (site measurements, design, the install extras), a group with no items
  const jobRoute = (job.routeLines ?? []).length + (job.bomLines ?? []).length > 0;
  if (jobRoute) groups.set('job', { key: 'job', ctx: new GroupCtx(null, env, null), items: [], recipe: null, kind: 'job', roomId: null });

  // the company percentages, with any the job set for itself on top, and any the Build priced under set over those
  const pricingUsed: LibPricing = { ...lib.pricing };
  const pricingOverrides: string[] = [];
  const buildPricingOverrides: string[] = [];
  const PRICING_KEYS = ['labor_contingency_pct', 'materials_markup_pct', 'sub_markup_pct', 'gross_margin_pct', 'sales_tax_pct', 'estimate_range_down_pct', 'estimate_range_up_pct'] as const;
  const isSet = (v: unknown): v is number | string => v !== undefined && v !== null && String(v).trim() !== '' && !!Rat.tryParse(v as number | string);
  for (const key of PRICING_KEYS) {
    const v = job.pricing?.[key];
    if (isSet(v)) {
      pricingUsed[key] = v;
      pricingOverrides.push(key);
    }
  }
  for (const key of PRICING_KEYS) {
    const v = opts.pricing?.[key];
    if (isSet(v)) {
      pricingUsed[key] = v;
      buildPricingOverrides.push(key);
    }
  }
  const pctOrZero = (v: unknown) => (v === undefined || v === null || String(v).trim() === '' ? Rat.ZERO : pct(v as number | string));
  const pricing = {
    contingency: pct(pricingUsed.labor_contingency_pct),
    materials: pct(pricingUsed.materials_markup_pct),
    subs: pct(pricingUsed.sub_markup_pct),
    margin: pct(pricingUsed.gross_margin_pct),
    tax: pctOrZero(pricingUsed.sales_tax_pct),
    rangeDown: pctOrZero(pricingUsed.estimate_range_down_pct),
    rangeUp: pctOrZero(pricingUsed.estimate_range_up_pct),
  };
  const keep = Rat.ONE.sub(pricing.margin);
  if (keep.sign() <= 0) throw new Error('Gross margin must be under 100%.');
  /** Labor with its contingency and the in-margin lines as charged (their markup and tax) take the margin; the outside lines as charged are added after it. */
  const priceOf = (labor: Rat, inCharged: Rat, outCharged: Rat = Rat.ZERO) =>
    labor.mul(Rat.ONE.add(pricing.contingency)).add(inCharged).div(keep).add(outCharged);
  // what goes over the margin and what is added after it, across the job (the totals say them apart; 2026-10-22)
  let inPre = Rat.ZERO;
  let inChargedAll = Rat.ZERO;
  let outChargedAll = Rat.ZERO;
  let taxInAll = Rat.ZERO;
  let taxOutAll = Rat.ZERO;
  // the same, as the sums of each line's own figures rounded to the cent (docs/job-view-brief.md, Boundaries: "every total is the
  // sum of its rounded lines, so the Materials tab, the Price tab and the item panels agree to the cent")
  let inLinesCents = 0;
  let outLinesCents = 0;
  let taxInLinesCents = 0;
  let taxOutLinesCents = 0;

  const itemLabor = new Map<string, Rat>(); // dollars
  const itemMinutes = new Map<string, Rat>();
  const itemMaterials = new Map<string, Rat>(); // dollars at cost, the outside-margin lines included
  const itemOutside = new Map<string, Rat>(); // dollars at cost: the part of itemMaterials priced outside the margin
  const itemInCharged = new Map<string, Rat>(); // the in-margin lines as charged before the margin: cost × (1 + markup) + tax
  const itemOutCharged = new Map<string, Rat>(); // the outside lines as charged: cost × (1 + markup) + tax
  const itemSubWork = new Map<string, Rat>(); // dollars: recipe lines that are subcontracted work, kept out of itemMaterials
  const itemTax = new Map<string, Rat>(); // dollars: the sales tax on what the item buys, passed through at cost (2026-10-17)
  const itemUnverifiedOps = new Map<string, UnverifiedStep[]>();
  const itemUnverifiedMats = new Map<string, UnverifiedLine[]>();
  const add = (m: Map<string, Rat>, id: string, v: Rat) => m.set(id, (m.get(id) ?? Rat.ZERO).add(v));
  const listed = <T,>(m: Map<string, T[]>, id: string, v: T) => m.set(id, [...(m.get(id) ?? []), v]);
  const poolKeyOf = (roomId: string | null) => (byRoom ? `room:${roomId ?? ''}` : 'job');
  const shareOut = (total: Rat, weights: Rat[]): Rat[] => {
    const sum = weights.reduce((a, w) => a.add(w), Rat.ZERO);
    const n = Rat.of(BigInt(Math.max(1, weights.length)));
    return weights.map((w) => (sum.isZero() ? total.div(n) : total.mul(w).div(sum)));
  };
  const sumOf = (xs: Rat[]) => xs.reduce((a, x) => a.add(x), Rat.ZERO);

  type Group = NonNullable<ReturnType<typeof groups.get>>;
  interface StepSrc { id: string | null; operation_id: string; qty_expr: string; charge_setup: boolean; note: string | null; own?: 'added' | 'changed'; own_index?: number }
  interface BomSrc { id: string | null; material_id: string | null; material_role: string | null; qty_expr: string; note: string | null; separate_pricing?: boolean; markup_pct?: Scalar; ignore_margin?: boolean; pricing_own?: boolean; own?: 'added' | 'changed'; own_index?: number; label?: string | null; unit_price_expr?: string | null; sub_expr?: string | null; link_expr?: string | null; unit_price?: Scalar; vendor?: string | null; quote_date?: string | null; link?: string | null }
  /** A step or line with its per-item quantities worked out, waiting for the pool's rounding. */
  interface PendingStep { g: Group; src: StepSrc; source: 'recipe' | 'custom'; index: number; weights: Rat[]; trace: Trace[]; warnings: string[]; op: LibOperation }
  interface PendingBom {
    g: Group; src: BomSrc; source: 'recipe' | 'custom'; index: number; weights: Rat[]; trace: Trace[]; warnings: string[];
    mat: LibMaterial | null; typed: boolean; label: string;
    /** For a typed price: each item's price per unit (zero where it has none). */
    prices: Rat[];
    /** Priced outside gross margin: Separate pricing with Ignore margin (2026-10-22). */
    outside: boolean;
    /** The markup this line is charged at: its own under Separate pricing, else the company materials markup. */
    markup: Rat;
    /** The pricing flags come from the item's own line, not the catalog. */
    pricingOwn: boolean;
    /** Subcontracted work: the sub markup, no margin, counted with the subs (Spencer, 2026-10-15). */
    isSub: boolean;
    /** Pays sales tax: every purchase, at the company rate on its cost (2026-10-22); sub work pays none. */
    taxed: boolean;
    /** The vendor page each item typed for this line, where the line has a link formula. */
    links: BomLink[];
  }
  const pendingSteps: PendingStep[] = [];
  const pendingBom: PendingBom[] = [];
  /** The pricing flags a job item's own line carries (2026-10-22). */
  const pricingOf = (line: { separate_pricing?: boolean; markup_pct?: Scalar; ignore_margin?: boolean }) => ({ separate_pricing: !!line.separate_pricing, markup_pct: line.markup_pct ?? null, ignore_margin: !!line.ignore_margin });
  /** True when an own line carries any pricing flag at all (a pricing-only change to a recipe line). */
  const hasPricing = (line: { separate_pricing?: boolean; markup_pct?: Scalar; ignore_margin?: boolean }) =>
    line.separate_pricing !== undefined || line.ignore_margin !== undefined || (line.markup_pct !== undefined && line.markup_pct !== null && String(line.markup_pct) !== '');
  const groupWarnings = new Map<string, string[]>();
  const gWarn = (g: Group, w: string) => {
    if (!groupWarnings.has(g.key)) groupWarnings.set(g.key, []);
    groupWarnings.get(g.key)!.push(w);
  };

  /** A step's quantity on each item of the group: the formula runs per item, never once for the set. */
  const perItem = (g: Group, expr: string, trace: Trace[], warnings: string[]): Rat[] => {
    if (g.items.length === 0) return [];
    const out: Rat[] = [];
    try {
      for (const it of g.items) {
        const v = toRat(calc(expr, it));
        out.push(v.sign() < 0 ? Rat.ZERO : v);
        if (v.sign() < 0) warnings.push(`${it.w.number || it.w.name}: the quantity came out negative; counted as zero.`);
        if (v.sign() !== 0) trace.push({ name: it.w.number || it.w.name, value: v.toDecimal(6) });
      }
    } catch (e) {
      warnings.push(`Formula problem: ${message(e)}`);
      return g.items.map(() => Rat.ZERO);
    }
    return out;
  };

  const planStep = (g: Group, src: StepSrc, source: 'recipe' | 'custom', index: number, weights: Rat[], trace: Trace[], warnings: string[]) => {
    const op = lib.operations[src.operation_id];
    if (!op) {
      gWarn(g, 'A route step points at an operation that is no longer in the library.');
      return;
    }
    pendingSteps.push({ g, src, source, index, weights, trace, warnings, op });
  };
  const planBom = (g: Group, src: BomSrc, source: 'recipe' | 'custom', index: number, weights: Rat[], trace: Trace[], warnings: string[]) => {
    // a price typed straight on the line (a custom line's unit cost), or one a formula reads from the item
    const literal = src.unit_price !== undefined;
    const typed = literal || (!!src.unit_price_expr && src.unit_price_expr.trim() !== '');
    const first = g.items[0];
    let mat: LibMaterial | null = null;
    if (src.material_id) mat = lib.materials[src.material_id] ?? null;
    else if (src.material_role && first) {
      try {
        mat = materialForRole(src.material_role, first, env);
      } catch {
        mat = null;
      }
    }
    // a typed line is called by its label, or by its material where it names one (an allowance line)
    const label = src.label?.trim() || mat?.description || '(typed price)';
    const qty = sumOf(weights);
    if (!mat && !typed && qty.sign() > 0) {
      warnings.push(src.material_role ? `No material chosen for "${src.material_role}" on this job, so this line prices at zero.` : 'The material is no longer in the library.');
    }
    // a typed price: each item's own figure (a Cabinotch price typed on the cabinet)
    const prices: Rat[] = [];
    if (typed) {
      g.items.forEach((it, i) => {
        if (weights[i]!.isZero()) return prices.push(Rat.ZERO);
        try {
          const v = literal ? (src.unit_price === null || src.unit_price === undefined || String(src.unit_price).trim() === '' ? null : String(src.unit_price).trim()) : calc(src.unit_price_expr!, it, trace);
          if (isBlank(v)) throw new Error('nothing typed yet');
          const p = toRat(v);
          if (p.sign() < 0) {
            warnings.push(`${label}, ${it.w.number || it.w.name}: the typed price came out negative; counted as zero.`);
            return prices.push(Rat.ZERO);
          }
          prices.push(p);
        } catch (e) {
          warnings.push(`${label}, ${it.w.number || it.w.name}: ${message(e)}, so it prices at zero.`);
          prices.push(Rat.ZERO);
        }
      });
    } else if (mat && (mat.price === null || mat.price === '') && qty.sign() > 0) warnings.push('This material has no price yet, so this line prices at zero.');
    // Separate pricing (Spencer, 2026-10-22): the line's own markup, and outside the margin when it ignores it
    const separate = !!src.separate_pricing;
    const ownMarkup = separate && src.markup_pct !== undefined && src.markup_pct !== null && String(src.markup_pct).trim() !== '';
    let markup = pricing.materials;
    if (ownMarkup) {
      try {
        markup = pct(src.markup_pct as number | string);
      } catch (e) {
        warnings.push(`Markup on this line: ${message(e)}; the company markup is used.`);
      }
    }
    // Spencer's sub rule (2026-10-15): an outside finisher's number on a line takes the sub markup and no margin
    let isSub = false;
    if (src.sub_expr && src.sub_expr.trim() !== '' && first) {
      try {
        isSub = truthy(calc(src.sub_expr, first));
      } catch (e) {
        warnings.push(`Sub work switch: ${message(e)}`);
      }
    }
    // where to buy it: the vendor page typed on each item that takes some of the line (Spencer, 2026-10-15)
    const links: BomLink[] = [];
    if (src.link && src.link.trim() !== '' && first) links.push({ item_id: first.w.id, number: first.w.number || first.w.name, url: src.link.trim() });
    if (src.link_expr && src.link_expr.trim() !== '') {
      g.items.forEach((it, i) => {
        if (weights[i]!.isZero()) return;
        try {
          const v = calc(src.link_expr!, it);
          if (!isBlank(v)) links.push({ item_id: it.w.id, number: it.w.number || it.w.name, url: String(v).trim() });
        } catch (e) {
          warnings.push(`Vendor link, ${it.w.number || it.w.name}: ${message(e)}`);
        }
      });
    }
    pendingBom.push({ g, src, source, index, weights, trace, warnings, mat, typed, label, prices, outside: separate && !!src.ignore_margin && !isSub, markup, pricingOwn: !!src.pricing_own, isSub, links, taxed: !isSub });
  };

  for (const g of groups.values()) {
    const first = g.items[0];
    if (g.kind === 'job') {
      // the job's own lines: the quantity is typed on the job, in the operation's unit; blank prices at zero and says so
      (job.routeLines ?? []).forEach((raw, i) => {
        // a Build's own hours on a job route line (Spencer, 2026-10-22: "Different job routes per build"), by the line's id or its type step
        const own = opts.routeQty?.[raw.id] ?? (raw.type_step_id ? opts.routeQty?.[`type:${raw.type_step_id}`] : undefined);
        const line = own !== undefined && own !== null && String(own).trim() !== '' ? { ...raw, qty: own } : raw;
        const warnings: string[] = [];
        let qty = Rat.ZERO;
        const blank = line.qty === null || line.qty === undefined || String(line.qty).trim() === '';
        if (blank) warnings.push('Nothing typed yet for this job, so this line prices at zero.');
        else {
          try {
            qty = toRat(convert(line.qty, 'number'));
          } catch (e) {
            warnings.push(message(e));
          }
        }
        planStep(g, { id: line.id, operation_id: line.operation_id, qty_expr: blank ? '' : String(line.qty), charge_setup: true, note: line.note ?? null }, 'custom', i, [qty], [], warnings);
      });
      (job.bomLines ?? []).forEach((line, i) => {
        const warnings: string[] = [];
        let qty = Rat.ZERO;
        try {
          qty = toRat(convert(line.qty, 'number'));
        } catch (e) {
          warnings.push(message(e));
        }
        planBom(g, { id: line.id, material_id: line.material_id, material_role: null, qty_expr: String(line.qty ?? ''), note: line.note ?? null }, 'custom', i, [qty], [], warnings);
      });
      continue;
    }
    const custom = first!.w.isCustom || !first!.w.cat;
    if (custom) {
      const row = first!.w.row;
      (row?.custom_route ?? []).forEach((line, i) => {
        const warnings: string[] = [];
        let qty = Rat.ZERO;
        try {
          qty = toRat(convert(line.qty, 'number'));
        } catch (e) {
          warnings.push(message(e));
        }
        planStep(g, { id: null, operation_id: line.operation_id, qty_expr: String(line.qty ?? ''), charge_setup: true, note: line.note ?? null }, 'custom', i, [qty], [], warnings);
      });
      (row?.custom_bom ?? []).forEach((line, i) => {
        const warnings: string[] = [];
        let qty = Rat.ZERO;
        try {
          qty = toRat(convert(line.qty, 'number'));
        } catch (e) {
          warnings.push(message(e));
        }
        // a typed line (a custom line: description, quantity, unit cost as quoted, vendor, date, link) has no material
        const typedLine = !line.material_id;
        planBom(
          g,
          typedLine
            ? { id: null, material_id: null, material_role: null, qty_expr: String(line.qty ?? ''), note: line.note ?? null, label: line.label ?? null, unit_price: line.unit_cost ?? null, vendor: line.vendor ?? null, quote_date: line.quote_date ?? null, link: line.link ?? null, ...pricingOf(line) }
            : { id: null, material_id: line.material_id, material_role: null, qty_expr: String(line.qty ?? ''), note: line.note ?? null, ...pricingOf(line) },
          'custom',
          i,
          [qty],
          [],
          warnings,
        );
      });
    } else if (g.recipe) {
      // the job item's own lines beside its recipe (2026-10-18): a change to a recipe step or line is matched by its id and
      // priced at the quantity typed on the item (per item, like a custom line); a removed one is left out; a line with
      // no step or line id is added after the recipe's. The group is this one item, so its row is the first item's.
      const own = first!.w.row;
      const ownRoute = own?.custom_route ?? [];
      const ownBom = own?.custom_bom ?? [];
      const typedQty = (q: Scalar, warnings: string[]): Rat[] => {
        let v = Rat.ZERO;
        try {
          v = toRat(convert(q, 'number'));
        } catch (e) {
          warnings.push(message(e));
        }
        return g.items.map(() => v);
      };
      const route = [...g.recipe.route].sort((a, b) => a.sort - b.sort);
      route.forEach((line, i) => {
        const k = ownRoute.findIndex((l) => l.step_id === line.id);
        const change = k >= 0 ? ownRoute[k]! : null;
        if (change?.removed) return;
        const trace: Trace[] = [];
        const warnings: string[] = [];
        if (change) {
          const src: StepSrc = { ...line, operation_id: change.operation_id || line.operation_id, qty_expr: String(change.qty ?? ''), note: change.note ?? line.note, own: 'changed', own_index: k };
          planStep(g, src, 'recipe', i, typedQty(change.qty, warnings), trace, warnings);
        } else planStep(g, line, 'recipe', i, perItem(g, line.qty_expr, trace, warnings), trace, warnings);
      });
      ownRoute.forEach((line, k) => {
        if (line.step_id) return;
        const warnings: string[] = [];
        planStep(g, { id: null, operation_id: line.operation_id, qty_expr: String(line.qty ?? ''), charge_setup: true, note: line.note ?? null, own: 'added', own_index: k }, 'custom', route.length + k, typedQty(line.qty, warnings), [], warnings);
      });
      const bom = [...g.recipe.bom].sort((a, b) => a.sort - b.sort);
      bom.forEach((line, i) => {
        const k = ownBom.findIndex((l) => l.line_id === line.id);
        const change = k >= 0 ? ownBom[k]! : null;
        if (change?.removed) return;
        const trace: Trace[] = [];
        const warnings: string[] = [];
        if (change) {
          // a change to the line itself (its quantity typed, its material swapped), or only to its pricing (Separate pricing, the
          // markup, Ignore margin: 2026-10-22), or both; a pricing-only change keeps the recipe's formula
          const qtyTyped = change.qty !== undefined && change.qty !== null && String(change.qty).trim() !== '';
          const swapped = !!change.material_id && change.material_id !== line.material_id;
          const lineChange = qtyTyped || swapped;
          const src: BomSrc = {
            ...line,
            ...(hasPricing(change) ? { ...pricingOf(change), pricing_own: true } : {}),
            ...(lineChange ? { material_id: change.material_id ?? line.material_id, material_role: change.material_id ? null : line.material_role, qty_expr: String(change.qty ?? ''), note: change.note ?? line.note, own: 'changed' as const, own_index: k } : { own_index: k }),
          };
          planBom(g, src, 'recipe', i, lineChange ? typedQty(change.qty, warnings) : perItem(g, line.qty_expr, trace, warnings), trace, warnings);
        } else planBom(g, line, 'recipe', i, perItem(g, line.qty_expr, trace, warnings), trace, warnings);
      });
      ownBom.forEach((line, k) => {
        if (line.line_id) return;
        const warnings: string[] = [];
        const typedLine = !line.material_id;
        planBom(
          g,
          typedLine
            ? { id: null, material_id: null, material_role: null, qty_expr: String(line.qty ?? ''), note: line.note ?? null, label: line.label ?? null, unit_price: line.unit_cost ?? null, vendor: line.vendor ?? null, quote_date: line.quote_date ?? null, link: line.link ?? null, own: 'added', own_index: k, ...pricingOf(line) }
            : { id: null, material_id: line.material_id, material_role: null, qty_expr: String(line.qty ?? ''), note: line.note ?? null, own: 'added', own_index: k, ...pricingOf(line) },
          'custom',
          bom.length + k,
          typedQty(line.qty, warnings),
          [],
          warnings,
        );
      });
    } else {
      gWarn(g, 'This catalog item has no recipe yet, so it prices at zero.');
    }
  }

  // 3b. the pool's rounding: what is bought or done whole is added across the pool (the job, or the room) and rounded
  // up once; each line takes its share of the whole units, each item its share of the line. Setup is paid once per
  // operation per pool, or once per batch of the pool's total when the operation has a batch size (Spencer, 2026-10-08).
  const stepTotals = new Map<string, Rat>();
  const bomTotals = new Map<string, Rat>();
  const stepKey = (p: PendingStep) => `${poolKeyOf(p.g.roomId)}|${p.op.id}`;
  // a material charged in whole packs is counted once for the job, whatever the pooling: the job buys the packs (2026-10-17)
  const bomKey = (p: PendingBom) => `${p.mat && !p.typed && materialPack(p.mat)?.whole ? 'job' : poolKeyOf(p.g.roomId)}|${p.mat ? p.mat.id : `${p.src.material_role ?? ''}:${p.label}`}`;
  // A quantity a person typed on the job route is priced as typed (Spencer, 2026-10-12): 1.75 hours of design is 1.75
  // hours, never rounded up to 2. Only what a formula works out is added across the pool and rounded up once.
  const asTyped = (p: PendingStep) => p.g.kind === 'job';
  for (const p of pendingSteps) if (!asTyped(p)) add(stepTotals, stepKey(p), sumOf(p.weights));
  for (const p of pendingBom) if (!p.typed) add(bomTotals, bomKey(p), sumOf(p.weights));
  /** The pool's total, rounded up to whole units, and the factor that scales every line's share up to it. */
  const rounded = (total: Rat): { whole: Rat; factor: Rat } => {
    if (total.sign() <= 0) return { whole: Rat.ZERO, factor: Rat.ONE };
    const whole = Rat.of(total.ceil());
    return { whole, factor: whole.div(total) };
  };

  const minutesByClass = new Map<string, Rat>();
  const dollarsByClass = new Map<string, Rat>();
  let allMinutes = Rat.ZERO;
  const groupSteps = new Map<string, ResolvedStep[]>();
  const groupBom = new Map<string, ResolvedBom[]>();
  const groupLabor = new Map<string, Rat>();
  const groupMinutes = new Map<string, Rat>();
  const groupMaterials = new Map<string, Rat>();
  const groupOutside = new Map<string, Rat>(); // dollars at cost: the lines priced outside the margin
  const groupInCharged = new Map<string, Rat>(); // the in-margin lines as charged before the margin
  const groupOutCharged = new Map<string, Rat>(); // the outside lines as charged
  const groupTaxIn = new Map<string, Rat>();
  const groupTaxOut = new Map<string, Rat>();
  const groupSubWork = new Map<string, Rat>();
  const groupTax = new Map<string, Rat>();
  for (const g of groups.values()) {
    groupSteps.set(g.key, []);
    groupBom.set(g.key, []);
  }

  // an operation's setup is paid once per pool (or once per batch of the pool's whole total) and shared out across every
  // step of that operation in the pool by quantity, so two identical boxes price the same wherever they sit (Spencer, 2026-10-14)
  const setupRaw = new Map<string, Rat>(); // pool key → the raw quantity of the steps that carry setup
  for (const p of pendingSteps) {
    if (!p.src.charge_setup || asTyped(p)) continue;
    const key = stepKey(p);
    setupRaw.set(key, (setupRaw.get(key) ?? Rat.ZERO).add(sumOf(p.weights)));
  }
  for (const p of pendingSteps) {
    const { g, src, op } = p;
    const key = stepKey(p);
    const raw = sumOf(p.weights);
    const pool = asTyped(p) ? raw : stepTotals.get(key) ?? Rat.ZERO;
    const { whole, factor } = asTyped(p) ? { whole: raw, factor: Rat.ONE } : rounded(pool);
    const qty = raw.mul(factor);
    const weights = p.weights.map((w) => w.mul(factor));
    const mpu = op.minutes_per_unit !== null && op.minutes_per_unit !== '' ? Rat.parse(op.minutes_per_unit) : null;
    if (mpu === null && qty.sign() > 0) p.warnings.push('This operation has no time standard yet.');
    const wantsSetup = src.charge_setup && qty.sign() > 0 && op.setup_minutes !== null && op.setup_minutes !== '';
    const batchSize = op.batch_size !== null && op.batch_size !== undefined && op.batch_size !== '' ? Rat.parse(op.batch_size) : null;
    const batches = wantsSetup ? (batchSize && batchSize.sign() > 0 ? Rat.of(whole.div(batchSize).ceil()) : Rat.ONE) : Rat.ZERO;
    const setupWhole = wantsSetup ? Rat.parse(op.setup_minutes!).mul(batches) : Rat.ZERO;
    // this step's share of the pool's one setup: by its raw quantity over the raw quantity of every setup-carrying step in the pool
    const carried = asTyped(p) ? raw : setupRaw.get(key) ?? raw;
    const setup = wantsSetup && carried.sign() > 0 ? setupWhole.mul(raw).div(carried) : Rat.ZERO;
    const minutes = qty.mul(mpu ?? Rat.ZERO).add(setup);
    const rateText = op.labor_class_id ? lib.rates[op.labor_class_id] : undefined;
    if (rateText === undefined && minutes.sign() > 0) p.warnings.push('No rate for this labor class in this standards version.');
    const rate = rateText !== undefined ? Rat.parse(rateText) : Rat.ZERO;
    const dollars = minutes.div(SIXTY).mul(rate);
    add(groupLabor, g.key, dollars);
    add(groupMinutes, g.key, minutes);
    allMinutes = allMinutes.add(minutes);
    if (op.labor_class_id) {
      add(minutesByClass, op.labor_class_id, minutes);
      add(dollarsByClass, op.labor_class_id, dollars);
    }
    const mins = shareOut(minutes, weights);
    const dols = shareOut(dollars, weights);
    const cents = g.items.length ? allocateCents(dollars.toCents(), dols) : [];
    const shares: Record<string, Share> = {};
    g.items.forEach((it, i) => {
      add(itemMinutes, it.w.id, mins[i]!);
      add(itemLabor, it.w.id, dols[i]!);
      if (!op.verified && minutes.sign() > 0 && weights[i]!.sign() > 0) listed(itemUnverifiedOps, it.w.id, { step_id: src.id, operation_id: op.id, operation_name: op.name });
      shares[it.w.id] = { minutes: mins[i]!.toDecimal(4), cents: cents[i]!, qty: weights[i]!.toDecimal(4) };
    });
    groupSteps.get(g.key)!.push({
      key: `${g.key}#r${p.index}`,
      source: p.source,
      step_id: src.id,
      ...(src.own ? { own: src.own, own_index: src.own_index } : {}),
      operation_id: op.id,
      operation_name: op.name,
      labor_class_id: op.labor_class_id,
      work_center_id: op.work_center_id,
      phase_id: op.phase_id,
      category: op.category,
      unit: op.unit,
      qty_expr: src.qty_expr,
      qty: qty.toDecimal(6),
      qty_raw: raw.toDecimal(6),
      pool_qty: whole.toDecimal(6),
      pool_qty_raw: pool.toDecimal(6),
      minutes_per_unit: (mpu ?? Rat.ZERO).toDecimal(6),
      setup_minutes: setup.toDecimal(4),
      setup_batches: Number(batches.toDecimal(0)),
      setup_elsewhere: false,
      setup_minutes_exact: setup.toDecimal(10),
      minutes_exact: minutes.toDecimal(10),
      minutes: minutes.toDecimal(4),
      hours: minutes.div(SIXTY).toDecimal(4),
      rate: rate.toFixed(2),
      cents: dollars.toCents(),
      trace: p.trace,
      note: src.note,
      warnings: p.warnings,
      verified: op.verified,
      shares,
    });
  }

  for (const p of pendingBom) {
    const { g, src, mat, typed, label } = p;
    const raw = sumOf(p.weights);
    const pool = typed ? raw : bomTotals.get(bomKey(p)) ?? Rat.ZERO;
    const { whole, factor } = typed ? { whole: raw, factor: Rat.ONE } : rounded(pool);
    const qty = raw.mul(factor);
    const weights = p.weights.map((w) => w.mul(factor));
    let price = Rat.ZERO;
    let dollars = Rat.ZERO;
    let dols: Rat[];
    const pack = !typed ? materialPack(mat) : null;
    let packOut: BomPack | undefined = pack ? { size: pack.size.toDecimal(6), price: pack.price.toFixed(2), each: pack.each.toDecimal(6), whole: pack.whole } : undefined;
    if (typed) {
      dols = g.items.map((_, i) => weights[i]!.mul(p.prices[i] ?? Rat.ZERO));
      dollars = sumOf(dols);
      price = qty.isZero() ? Rat.ZERO : dollars.div(qty);
    } else if (pack && pack.whole) {
      // the job's pieces round up to whole packs once; this line pays its share of the packs by the pieces it uses
      const packs = packsFor(whole, pack.size);
      dollars = pool.isZero() ? Rat.ZERO : packs.mul(pack.price).mul(raw).div(pool);
      price = qty.isZero() ? pack.each : dollars.div(qty);
      dols = shareOut(dollars, weights);
      packOut = { ...packOut!, packs: packs.toDecimal(6), pieces: whole.toDecimal(6), left_over: packs.mul(pack.size).sub(whole).toDecimal(6) };
    } else {
      price = pack ? pack.each : mat && mat.price !== null && mat.price !== '' ? Rat.parse(mat.price) : Rat.ZERO;
      dollars = qty.mul(price);
      dols = shareOut(dollars, weights);
    }
    if (p.isSub) add(groupSubWork, g.key, dollars);
    else add(groupMaterials, g.key, dollars);
    // sales tax on every purchase: the company rate on the line's cost (Spencer, 2026-10-22); in the margin with the line
    const tax = p.taxed ? dollars.mul(pricing.tax) : Rat.ZERO;
    if (!tax.isZero()) add(groupTax, g.key, tax);
    // what the line charges before the margin: its cost at its markup, with its tax; outside lines are added after the margin
    const charged = p.isSub ? Rat.ZERO : dollars.mul(Rat.ONE.add(p.markup));
    if (!p.isSub) {
      if (p.outside) {
        add(groupOutside, g.key, dollars);
        add(groupOutCharged, g.key, charged.add(tax));
        add(groupTaxOut, g.key, tax);
        outLinesCents += charged.toCents() + tax.toCents();
        taxOutLinesCents += tax.toCents();
      } else {
        add(groupInCharged, g.key, charged.add(tax));
        add(groupTaxIn, g.key, tax);
        inLinesCents += charged.toCents() + tax.toCents();
        taxInLinesCents += tax.toCents();
      }
    }
    const cents = g.items.length ? allocateCents(dollars.toCents(), dols) : [];
    const shares: Record<string, Share> = {};
    g.items.forEach((it, i) => {
      if (p.isSub) add(itemSubWork, it.w.id, dols[i]!);
      else {
        add(itemMaterials, it.w.id, dols[i]!);
        const t = p.taxed ? dols[i]!.mul(pricing.tax) : Rat.ZERO;
        const ch = dols[i]!.mul(Rat.ONE.add(p.markup)).add(t);
        if (p.outside) {
          add(itemOutside, it.w.id, dols[i]!);
          add(itemOutCharged, it.w.id, ch);
        } else add(itemInCharged, it.w.id, ch);
      }
      if (!tax.isZero()) add(itemTax, it.w.id, dols[i]!.mul(pricing.tax));
      if (mat && !mat.verified && qty.sign() > 0 && weights[i]!.sign() > 0) listed(itemUnverifiedMats, it.w.id, { line_id: src.id, material_id: mat.id, description: mat.description });
      shares[it.w.id] = { cents: cents[i]!, qty: weights[i]!.toDecimal(4) };
    });
    groupBom.get(g.key)!.push({
      key: `${g.key}#b${p.index}`,
      source: p.source,
      line_id: src.id,
      ...(src.own ? { own: src.own, own_index: src.own_index } : {}),
      material_id: mat?.id ?? null,
      material_role: src.material_role,
      description: mat?.description ?? (typed ? label : src.material_role ? `(${src.material_role})` : '(missing material)'),
      unit: mat?.unit ?? null,
      vendor: mat?.vendor ?? src.vendor ?? null,
      ...(src.quote_date ? { quote_date: src.quote_date } : {}),
      qty_expr: src.qty_expr,
      qty: qty.toDecimal(6),
      qty_raw: raw.toDecimal(6),
      pool_qty: whole.toDecimal(6),
      pool_qty_raw: pool.toDecimal(6),
      price: price.toFixed(2),
      cents: dollars.toCents(),
      trace: p.trace,
      note: src.note,
      warnings: p.warnings,
      verified: mat?.verified ?? false,
      shares,
      typed_price: typed,
      separate_pricing: !!p.src.separate_pricing && !p.isSub,
      markup_pct: p.markup.mul(HUNDRED).toDecimal(2),
      outside_margin: p.outside,
      ...(p.pricingOwn ? { pricing_own: true } : {}),
      charged_cents: charged.toCents(),
      sub_work: p.isSub,
      links: p.links,
      ...(packOut ? { pack: packOut } : {}),
      ...(!tax.isZero() ? { tax_cents: tax.toCents() } : {}),
    });
  }

  const outGroups: ResolvedGroup[] = [];
  const groupPrice = new Map<string, Rat>(); // exact, before the one rounding for the job
  const groupItemPrice = new Map<string, Rat[]>();
  for (const g of groups.values()) {
    const first = g.items[0];
    const labor = groupLabor.get(g.key) ?? Rat.ZERO;
    const materials = groupMaterials.get(g.key) ?? Rat.ZERO;
    const outside = groupOutside.get(g.key) ?? Rat.ZERO;
    const tax = groupTax.get(g.key) ?? Rat.ZERO;
    const inCharged = groupInCharged.get(g.key) ?? Rat.ZERO;
    const outCharged = groupOutCharged.get(g.key) ?? Rat.ZERO;
    const price = priceOf(labor, inCharged, outCharged);
    groupPrice.set(g.key, price);
    inPre = inPre.add(labor.mul(Rat.ONE.add(pricing.contingency))).add(inCharged);
    inChargedAll = inChargedAll.add(inCharged);
    outChargedAll = outChargedAll.add(outCharged);
    taxInAll = taxInAll.add(groupTaxIn.get(g.key) ?? Rat.ZERO);
    taxOutAll = taxOutAll.add(groupTaxOut.get(g.key) ?? Rat.ZERO);
    groupItemPrice.set(
      g.key,
      g.items.map((it) => priceOf(itemLaborOf(it.w.id), itemInCharged.get(it.w.id) ?? Rat.ZERO, itemOutCharged.get(it.w.id) ?? Rat.ZERO)),
    );
    const custom = g.kind === 'items' && (first!.w.isCustom || !first!.w.cat);
    outGroups.push({
      key: g.key,
      kind: g.kind,
      catalog_item_id: g.kind === 'job' || custom ? null : first!.w.cat?.id ?? null,
      name: g.kind === 'job' ? 'Job route' : custom ? first!.w.name : first!.w.cat?.name ?? first!.w.name,
      recipe_id: g.recipe?.id ?? null,
      recipe_name: g.recipe?.name ?? null,
      item_ids: g.items.map((it) => it.w.id),
      steps: groupSteps.get(g.key)!,
      bom: groupBom.get(g.key)!,
      labor_minutes: (groupMinutes.get(g.key) ?? Rat.ZERO).toDecimal(4),
      labor_cents: labor.toCents(),
      material_cents: materials.toCents(),
      outside_margin_cents: outside.toCents(),
      in_charged_cents: inCharged.toCents(),
      out_charged_cents: outCharged.toCents(),
      sub_work_cents: (groupSubWork.get(g.key) ?? Rat.ZERO).toCents(),
      ...(!tax.isZero() ? { tax_cents: tax.toCents() } : {}),
      price_cents: 0, // the job's one rounding is shared out below
      warnings: groupWarnings.get(g.key) ?? [],
    });
  }

  // the price is rounded to the cent once, for the job; each group gets its share, so
  // how the items are grouped never moves the total
  const exactPrices = outGroups.map((g) => groupPrice.get(g.key)!);
  const itemsPriceExact = exactPrices.reduce((a, p) => a.add(p), Rat.ZERO);
  const groupCents = allocateCents(itemsPriceExact.toCents(), exactPrices);
  const groupPriceCents = new Map<string, number>();
  outGroups.forEach((g, i) => {
    g.price_cents = groupCents[i]!;
    groupPriceCents.set(g.key, groupCents[i]!);
  });

  function itemLaborOf(id: string): Rat {
    return itemLabor.get(id) ?? Rat.ZERO;
  }
  function itemMaterialsOf(id: string): Rat {
    return itemMaterials.get(id) ?? Rat.ZERO;
  }

  // 4. items
  const itemPriceCents = new Map<string, number>();
  const itemLaborCents = new Map<string, number>();
  const itemMaterialCents = new Map<string, number>();
  const itemOutsideCents = new Map<string, number>();
  const itemTaxCents = new Map<string, number>();
  for (const g of groups.values()) {
    const og = outGroups.find((x) => x.key === g.key)!;
    const prices = allocateCents(groupPriceCents.get(g.key)!, groupItemPrice.get(g.key)!);
    const labors = allocateCents(og.labor_cents, g.items.map((it) => itemLaborOf(it.w.id)));
    const mats = allocateCents(og.material_cents, g.items.map((it) => itemMaterialsOf(it.w.id)));
    const outs = allocateCents(og.outside_margin_cents, g.items.map((it) => itemOutside.get(it.w.id) ?? Rat.ZERO));
    const taxes = allocateCents(og.tax_cents ?? 0, g.items.map((it) => itemTax.get(it.w.id) ?? Rat.ZERO));
    g.items.forEach((it, i) => {
      itemPriceCents.set(it.w.id, prices[i]!);
      itemLaborCents.set(it.w.id, labors[i]!);
      itemMaterialCents.set(it.w.id, mats[i]!);
      itemOutsideCents.set(it.w.id, outs[i]!);
      if (taxes[i]) itemTaxCents.set(it.w.id, taxes[i]!);
    });
  }

  const stdKeys = new Set(lib.standards.map((d) => d.key));
  const namesReadBy = new Map<string, string[]>();
  const namesRead = (cat: LibCatalogItem): string[] => {
    let names = namesReadBy.get(cat.id);
    if (!names) {
      const set = new Set<string>();
      const take = (expr: string | null) => {
        if (!expr) return;
        try {
          identifiers(expr).forEach((n) => set.add(n));
        } catch {
          /* a broken formula is reported where it is evaluated */
        }
      };
      cat.derived.forEach((d) => take(d.expr));
      cat.inputs.forEach((i) => {
        take(i.rule_expr);
        take(i.hidden_expr ?? null);
        if (i.data_type === 'standard') set.add(i.standard_key?.trim() || i.key);
      });
      if (cat.recipe_standard?.trim()) set.add(cat.recipe_standard.trim());
      cat.recipes.forEach((r) => {
        take(r.unavailable_expr ?? null);
        r.route.forEach((s) => take(s.qty_expr));
        r.bom.forEach((b) => {
          take(b.qty_expr);
          take(b.unit_price_expr ?? null);
          take(b.sub_expr ?? null);
          take(b.link_expr ?? null);
          if (b.material_role) set.add(b.material_role);
        });
        r.parts.forEach((p) => {
          [p.qty_expr, p.length_expr, p.width_expr, p.band_long_expr, p.band_short_expr, p.band_material_expr ?? null].forEach(take);
          if (p.material_role) set.add(p.material_role);
        });
      });
      cat.children.forEach((c) => {
        take(c.count_expr);
        take(c.child_expr);
        Object.values(c.input_map).forEach(take);
      });
      // thickness_<role> and sheet_area_<role> read the material filling that role
      for (const n of [...set]) {
        const m = /^(?:thickness|sheet_area)_(.+)$/.exec(n);
        if (m) set.add(m[1]!);
      }
      // a choice standard is read wherever one of the material roles it fills is used
      for (const def of lib.standards) {
        if (def.choices?.some((ch) => Object.keys(ch.materials ?? {}).some((role) => set.has(role)))) set.add(def.key);
      }
      names = [...set];
      namesReadBy.set(cat.id, names);
    }
    return names;
  };

  /** The add-on an item is priced with: its own row's, or its parent's (a child follows its parent). */
  const addOnOf = (w: WorkItem): string | null => w.row?.add_on_id ?? (w.parent ? addOnOf(w.parent) : null);
  const outItems: ResolvedItem[] = work.map((w) => {
    const ctx = ctxOf.get(w.id)!;
    const cat = w.isCustom ? null : w.cat;
    const inputs: ResolvedInput[] = (cat?.inputs ?? [])
      .slice()
      .sort((a, b) => a.sort - b.sort)
      .map((def) => {
        let value: Value = null;
        try {
          value = ctx.get(def.key) ?? null;
        } catch (e) {
          w.warnings.push(`${def.label}: ${message(e)}`);
        }
        const source = ctx.sourceOf(def.key);
        const followed = def.data_type === 'standard' ? ctx.followed(def.key) : null;
        if (def.data_type === 'standard' && !followed) w.warnings.push(`${def.label} follows the standard "${def.standard_key?.trim() || def.key}", which does not exist.`);
        let hidden = false;
        if (def.hidden_expr && def.hidden_expr.trim() !== '') {
          try {
            hidden = truthy(calc(def.hidden_expr, ctx));
          } catch (e) {
            w.warnings.push(`Hide rule on ${def.label}: ${message(e)}`);
          }
        }
        return {
          key: def.key,
          label: def.label,
          info: def.info ?? null,
          unit: followed ? followed.unit ?? null : def.unit,
          data_type: followed ? followed.data_type : def.data_type,
          choices: followed ? followed.choices?.map((c) => c.value) ?? null : def.choices,
          value: scalar(value),
          source,
          locked: source === 'parent' || source === 'standard',
          ...(hidden ? { hidden } : {}),
          ...(def.data_type === 'material' ? { material_filter: def.material_filter ?? null } : {}),
          // a catalog input is edited on the item like any other input: it carries no standard key here
          ...(followed && followed.kind !== 'catalog' ? { standard_key: followed.key, can_override: followed.allow_item_override !== false } : {}),
        };
      });

    const derived = (cat?.derived ?? [])
      .slice()
      .sort((a, b) => a.sort - b.sort)
      .map((d) => {
        let value = '';
        try {
          value = String(scalar(ctx.get(d.key) ?? null) ?? '');
        } catch (e) {
          w.warnings.push(`${d.label}: ${message(e)}`);
        }
        return { key: d.key, label: d.label, expr: d.expr, value, trace: ctx.traces.get(d.key) ?? [] };
      });

    // the standards this item reads: its own formulas, and the mapping its parent sized it with
    const reads = new Set<string>();
    if (cat) namesRead(cat).forEach((n) => reads.add(n));
    if (w.viaChild) {
      Object.values(w.viaChild.input_map).forEach((expr) => {
        try {
          identifiers(expr).forEach((n) => reads.add(n));
        } catch {
          /* reported at evaluation */
        }
      });
    }
    // an input that follows a standard shows as the input, not again under the standards the item reads
    const inputKeys = new Set((cat?.inputs ?? []).map((i) => (i.data_type === 'standard' ? i.standard_key?.trim() || i.key : i.key)));
    const standards_used: ResolvedStandardUse[] = lib.standards
      .filter((d) => d.kind !== 'catalog' && reads.has(d.key) && stdKeys.has(d.key) && !inputKeys.has(d.key))
      .sort((a, b) => a.sort - b.sort)
      .map((d) => {
        const o = w.overrides[d.key];
        const overridden = o !== undefined && o !== null && o !== '';
        const inForce = ctx.std; // the job's value, or the room's own
        return {
          key: d.key,
          label: d.label,
          value: scalar(overridden ? convert(o, d.data_type) : inForce.get(d.key) ?? null),
          job_value: scalar(inForce.get(d.key) ?? null),
          overridden,
        };
      });

    const rule_warnings: string[] = [];
    for (const def of cat?.inputs ?? []) {
      if (!def.rule_expr) continue;
      // an input hidden by its rule is not there: its own rule stays quiet (a Cabinotch price on an In-House box)
      if (inputs.find((i) => i.key === def.key)?.hidden) continue;
      try {
        if (truthy(calc(def.rule_expr, ctx))) rule_warnings.push(def.rule_message ?? `${def.label} breaks a catalog rule.`);
      } catch (e) {
        w.warnings.push(`Rule on ${def.label}: ${message(e)}`);
      }
    }

    // the item's cut list: its parts with their numbers, and the material each is cut from
    let parts: ResolvedPart[] | undefined;
    if (w.recipe && w.recipe.parts.length > 0 && !w.isCustom) {
      let calcs: PartCalc[] = [];
      try {
        calcs = ctx.partCalcs();
      } catch (e) {
        w.warnings.push(`Parts: ${message(e)}`);
      }
      parts = calcs.map((c) => {
        const material = c.part.material_id ? lib.materials[c.part.material_id] ?? null : c.part.material_role ? materialForRole(c.part.material_role, ctx, env) : null;
        // the banding roll on its banded edges: a role the job fills, or a material by id or exact description
        const banded = c.bandLong.add(c.bandShort).sign() > 0;
        let bandMat: LibMaterial | null = null;
        let bandNote: string | null = null;
        if (banded && c.part.band_material_expr && c.part.band_material_expr.trim() !== '') {
          try {
            const v = calc(c.part.band_material_expr, ctx);
            const key = typeof v === 'string' ? v.trim() : '';
            if (key) {
              bandMat = materialForRole(key, ctx, env) ?? lib.materials[key] ?? Object.values(lib.materials).find((m) => m.description.trim().toLowerCase() === key.toLowerCase()) ?? null;
              if (!bandMat) bandNote = `(${key}: not chosen on this job)`;
            }
          } catch (e) {
            w.warnings.push(`Part "${c.part.name}", banding roll: ${message(e)}`);
          }
        }
        return {
          part_id: c.part.id,
          name: c.part.name,
          // what the part is called on the job: the item's number, a dash, its label or its name (Spencer, 2026-10-07)
          label: `${w.number || w.name}-${c.part.label?.trim() || c.part.name}`,
          tally: c.tally,
          qty: c.qty.toDecimal(4),
          length: c.length.toDecimal(6),
          width: c.width.toDecimal(6),
          area_sq_in: c.area.toDecimal(10), // exact for sizes to 1/32: the cut list adds these up
          band_long: c.bandLong.toDecimal(2),
          band_short: c.bandShort.toDecimal(2),
          band_inches: c.band.toDecimal(10),
          grain: c.part.grain,
          grain_locked: !!c.part.grain_locked,
          material_id: material?.id ?? null,
          material_role: c.part.material_role,
          description: material?.description ?? (c.part.material_role ? `(${c.part.material_role}: not chosen on this job)` : '(no material named)'),
          band_material_id: bandMat?.id ?? null,
          band_description: banded ? bandMat?.description ?? bandNote ?? '(no roll named)' : null,
          note: c.part.note,
        };
      });
    }

    // which of its catalog item's recipes this item may use, by each recipe's own rule over this item's inputs (legs = 4);
    // an item resolving through an unavailable one is flagged and priced through it anyway (Spencer, 2026-10-07)
    let recipes: RecipeAvailability[] | undefined;
    if (cat && !w.isCustom) {
      recipes = cat.recipes.map((r) => {
        const expr = r.unavailable_expr?.trim();
        let unavailable: string | null = null;
        if (expr) {
          try {
            unavailable = truthy(calc(expr, ctx)) ? expr : null;
          } catch (e) {
            w.warnings.push(`Rule on the ${r.name} recipe: ${message(e)}`);
          }
        }
        return { recipe_id: r.id, name: r.name, unavailable };
      });
      const mine = w.recipe ? recipes.find((r) => r.recipe_id === w.recipe!.id) : undefined;
      if (mine?.unavailable) w.warnings.push(`Its ${mine.name} recipe is not available for this item (${mine.unavailable}); it is priced through it anyway. Pick another recipe for this item, or change what makes it unavailable.`);
    }
    // a choice this item reads that is unavailable on this job, or in its room
    for (const def of lib.standards) {
      if (!reads.has(def.key)) continue;
      const reason = unavailableChoice(def, ctx.std.get(def.key), ctx.std);
      if (reason) w.warnings.push(`${def.label} "${scalar(ctx.std.get(def.key) ?? null)}" is not available here (${reason}).`);
    }

    const ops = itemUnverifiedOps.get(w.id) ?? [];
    const mats = itemUnverifiedMats.get(w.id) ?? [];
    // the flag follows the recipe this item resolved through, not a sibling recipe of the item
    const unverifiedRecipe = cat && w.recipe ? !w.recipe.verified : false;
    return {
      id: w.id,
      stored: w.stored,
      number: w.number,
      name: w.name,
      catalog_item_id: cat?.id ?? null,
      catalog_item_name: cat?.name ?? null,
      recipe_id: w.isCustom ? null : w.recipe?.id ?? null,
      recipe_name: w.isCustom ? null : w.recipe?.name ?? null,
      group_key: groupKeyOf.get(w.id)!,
      parent_item_id: w.parent?.id ?? null,
      room_id: w.room_id,
      child_index: w.childIndex,
      child_link_id: w.viaChild?.id ?? null,
      is_custom: w.isCustom || !w.cat,
      ...(addOnOf(w) ? { add_on_id: addOnOf(w) } : {}),
      ...(w.row?.replaces_item_id ? { replaces_item_id: w.row.replaces_item_id } : {}),
      ...(w.row?.replaces_item_id && (w.row.children_left_out ?? []).length ? { children_left_out: w.row.children_left_out } : {}),
      mark_threshold: cat?.mark_threshold ?? (w.parent ? 'sub_item' : 'top_tier'),
      inputs,
      standards_used,
      derived,
      ...(recipes ? { recipes } : {}),
      ...(parts ? { parts } : {}),
      labor_minutes: (itemMinutes.get(w.id) ?? Rat.ZERO).toDecimal(4),
      labor_cents: itemLaborCents.get(w.id) ?? 0,
      material_cents: itemMaterialCents.get(w.id) ?? 0,
      outside_margin_cents: itemOutsideCents.get(w.id) ?? 0,
      ...(itemTaxCents.get(w.id) ? { tax_cents: itemTaxCents.get(w.id) } : {}),
      price_cents: itemPriceCents.get(w.id) ?? 0,
      sub_cents: 0, // filled below, from the sub lines that belong to this item
      sub_price_cents: 0,
      rollup_price_cents: 0, // filled below, once every item has its own price
      rollup_labor_minutes: '0',
      warnings: [...new Set(w.warnings)],
      rule_warnings,
      unverified: {
        recipe: unverifiedRecipe,
        operations: ops.length,
        materials: mats.length,
        any: unverifiedRecipe || ops.length > 0 || mats.length > 0 || w.isCustom,
        steps: ops,
        lines: mats,
      },
      overrides_count: Object.values(w.overrides).filter((v) => v !== null && v !== undefined && v !== '').length,
      note: w.note,
      sort: w.sort,
    };
  });

  // subs: a number typed by a person, never worked out here; priced at the sub markup and no margin (Spencer,
  // 2026-10-15: a sub carries the sub markup only; the shop's own hours around it keep the full margin)
  let subs = Rat.ZERO;
  // a sub line may carry its own markup over the job's (Spencer, 2026-10-22); the job's subs price is the sum of each
  // line's price, exact, rounded once
  let subsPriceExact = Rat.ZERO;
  const lineMarkup = (v: unknown): Rat => (v === undefined || v === null || String(v).trim() === '' ? pricing.subs : pct(v as number | string));
  const subPriceOf = (amount: Rat, markup: Rat = pricing.subs) => amount.mul(Rat.ONE.add(markup));
  // a recipe line that is sub work (an outside finisher's number typed or worked out on the item) is a sub of that item
  for (const i of outItems) {
    const work = itemSubWork.get(i.id);
    if (!work || work.isZero()) continue;
    subs = subs.add(work);
    subsPriceExact = subsPriceExact.add(subPriceOf(work));
    i.sub_cents += work.toCents();
    i.sub_price_cents += subPriceOf(work).toCents();
  }
  const subLinesOff: SubLineOff[] = [];
  for (const s of job.subLines) {
    // a sub that belongs to an item left out of this price (in an add-on that is off, or replaced) goes with it
    const owner = s.job_item_id ? everyRow.get(s.job_item_id) : undefined;
    if (owner && !priced(owner)) continue;
    try {
      const quote = convert(s.quote_amount, 'number');
      const est = convert(s.internal_estimate, 'number');
      let amount = toRat(s.use_quote && quote !== null ? quote : est);
      const ownerCtx = s.job_item_id ? ctxOf.get(s.job_item_id) : undefined;
      // the line's cell under this Build (Spencer, 2026-10-24): left out of this price by hand, or a cost typed for the build
      const cell = opts.buildId ? s.build_overrides?.[opts.buildId] : undefined;
      if (cell?.left_out) {
        subLinesOff.push({ sub_line_id: s.id, scope_text: s.scope_text, item_id: ownerCtx?.w.id ?? s.job_item_id ?? '', cents: amount.toCents(), rule: 'left out on the Subs tab', by_hand: true });
        continue;
      }
      if (cell && cell.amount !== undefined && cell.amount !== null && String(cell.amount).trim() !== '') amount = toRat(convert(cell.amount, 'number'));
      // the item's own rule: an outside finisher's estimate prices only when something goes to the finisher (2026-10-17)
      const rule = ownerCtx?.w.cat?.sub_lines_off_expr?.trim();
      if (ownerCtx && rule) {
        let off = false;
        try {
          off = truthy(calc(rule, ownerCtx));
        } catch (e) {
          warnings.push(`Sub line "${s.scope_text}": the rule that leaves it out (${rule}) could not be read, so it is priced: ${message(e)}`);
        }
        if (off) {
          subLinesOff.push({ sub_line_id: s.id, scope_text: s.scope_text, item_id: ownerCtx.w.id, cents: amount.toCents(), rule });
          continue;
        }
      }
      subs = subs.add(amount);
      const markup = lineMarkup(s.markup_pct);
      subsPriceExact = subsPriceExact.add(subPriceOf(amount, markup));
      // a line that belongs to an item (an outside finishing line) rides on that item's line
      const owner = s.job_item_id ? outItems.find((i) => i.id === s.job_item_id) : undefined;
      if (owner) {
        owner.sub_cents += amount.toCents();
        owner.sub_price_cents += subPriceOf(amount, markup).toCents();
      } else if (s.job_item_id) {
        warnings.push(`Sub line "${s.scope_text}" belongs to an item that is no longer on the job; it is counted with the job's subs.`);
      }
    } catch (e) {
      warnings.push(`Sub line "${s.scope_text}": ${message(e)}`);
    }
  }

  // the cabinet line reads as the whole cabinet: its own dollars plus everything it creates
  const rollPrice = new Map<string, number>();
  const rollMinutes = new Map<string, Rat>();
  const roll = (id: string): { cents: number; minutes: Rat } => {
    if (rollPrice.has(id)) return { cents: rollPrice.get(id)!, minutes: rollMinutes.get(id)! };
    const me = outItems.find((i) => i.id === id)!;
    let cents = me.price_cents + me.sub_price_cents;
    let mins = Rat.parse(me.labor_minutes);
    for (const c of outItems) {
      if (c.parent_item_id !== id) continue;
      const r = roll(c.id);
      cents += r.cents;
      mins = mins.add(r.minutes);
    }
    rollPrice.set(id, cents);
    rollMinutes.set(id, mins);
    return { cents, minutes: mins };
  };
  for (const i of outItems) {
    const r = roll(i.id);
    i.rollup_price_cents = r.cents;
    i.rollup_labor_minutes = r.minutes.toDecimal(4);
  }

  // 5. totals
  let laborCents = 0;
  let materialCents = 0;
  let outsideCents = 0;
  const itemsPrice = itemsPriceExact.toCents();
  const minutes = allMinutes;
  for (const g of outGroups) {
    laborCents += g.labor_cents;
    materialCents += g.material_cents;
    outsideCents += g.outside_margin_cents;
  }
  const subsPrice = subsPriceExact.toCents();
  // job-wide costs: priced as a material line is (the company markup and the tax, inside the margin), or with their own
  // separate pricing (their markup; outside the margin when they ignore it)
  let costLines = Rat.ZERO;
  let costLinesPrice = Rat.ZERO;
  let costTax = Rat.ZERO;
  for (const c of job.costLines ?? []) {
    try {
      const amount = toRat(convert(c.amount, 'number'));
      costLines = costLines.add(amount);
      const own = c.separate_pricing && c.markup_pct !== undefined && c.markup_pct !== null && String(c.markup_pct).trim() !== '';
      const mk = own ? pct(c.markup_pct as number | string) : pricing.materials;
      const tax = amount.mul(pricing.tax);
      costTax = costTax.add(tax);
      const charged = amount.mul(Rat.ONE.add(mk)).add(tax);
      const lineCents = amount.mul(Rat.ONE.add(mk)).toCents() + tax.toCents();
      if (c.separate_pricing && c.ignore_margin) {
        outLinesCents += lineCents;
        taxOutLinesCents += tax.toCents();
        outChargedAll = outChargedAll.add(charged);
        taxOutAll = taxOutAll.add(tax);
        costLinesPrice = costLinesPrice.add(charged);
      } else {
        inLinesCents += lineCents;
        taxInLinesCents += tax.toCents();
        inPre = inPre.add(charged);
        inChargedAll = inChargedAll.add(charged);
        taxInAll = taxInAll.add(tax);
        costLinesPrice = costLinesPrice.add(priceOf(Rat.ZERO, charged));
      }
    } catch (e) {
      warnings.push(`Job-wide cost "${c.label}": ${message(e)}`);
    }
  }
  // the job's sales tax, rounded once from the exact sum, as the price is
  // the sum of every line's tax, each rounded to the cent (the Materials tab's two tax lines add up to it)
  const taxCents = taxInLinesCents + taxOutLinesCents;
  void costTax;
  let decisions = Rat.ZERO;
  for (const d of job.decisionLines) {
    try {
      decisions = decisions.add(toRat(convert(d.amount, 'number')));
    } catch (e) {
      warnings.push(`Decision line "${d.label}": ${message(e)}`);
    }
  }
  const price = itemsPrice + subsPrice + costLinesPrice.toCents() + decisions.toCents();
  // the sales tax is a cost the shop pays, passed through at cost: in the raw cost as in the price (2026-10-17)
  const rawCost = laborCents + materialCents + subs.toCents() + costLines.toCents() + taxCents;
  const margin = price - rawCost;
  const hours_by_class: Record<string, string> = {};
  for (const [id, m] of minutesByClass) hours_by_class[id] = m.div(SIXTY).toDecimal(4);
  const cents_by_class: Record<string, number> = {};
  for (const [id, d] of dollarsByClass) cents_by_class[id] = d.toCents();
  // the estimate range: the price widened down and up by the two percentages, each rounded to the cent (Spencer, 2026-10-22)
  const priceRat = Rat.of(BigInt(price), 100n);
  const rangeLow = price - priceRat.mul(pricing.rangeDown).toCents();
  const rangeHigh = price + priceRat.mul(pricing.rangeUp).toCents();

  const lineWarnings = outGroups.reduce(
    (n, g) => n + g.warnings.length + g.steps.reduce((a, s) => a + s.warnings.length, 0) + g.bom.reduce((a, b) => a + b.warnings.length, 0),
    0,
  );
  const standards: Record<string, Scalar> = {};
  for (const [k, v] of std) standards[k] = scalar(v);

  return {
    standards_version_id: lib.standardsVersion.id,
    standards_version_name: lib.standardsVersion.name,
    standards,
    pricing: pricingUsed,
    pricing_overrides: pricingOverrides,
    ...(buildPricingOverrides.length ? { build_pricing_overrides: buildPricingOverrides } : {}),
    items: outItems,
    groups: outGroups,
    ...(subLinesOff.length ? { sub_lines_off: subLinesOff } : {}),
    totals: {
      hours_by_class,
      cents_by_class,
      labor_minutes: minutes.toDecimal(4),
      labor_hours: minutes.div(SIXTY).toDecimal(4),
      labor_cents: laborCents,
      material_cents: materialCents,
      outside_margin_cents: outsideCents,
      in_margin_pre_cents: inPre.toCents(),
      // the Materials tab's in-margin column total (its lines' figures, rounded each) with the margin applied, rounded once
      materials_in_charged_cents: Rat.of(BigInt(inLinesCents), 100n).div(keep).toCents(),
      materials_out_charged_cents: outLinesCents,
      tax_in_cents: taxInLinesCents,
      tax_out_cents: taxOutLinesCents,
      gross_margin_pct: pricing.margin.mul(HUNDRED).toDecimal(2),
      range_down_pct: pricing.rangeDown.mul(HUNDRED).toDecimal(2),
      range_up_pct: pricing.rangeUp.mul(HUNDRED).toDecimal(2),
      range_low_cents: rangeLow,
      range_high_cents: rangeHigh,
      sub_cents: subs.toCents(),
      cost_lines_cents: costLines.toCents(),
      tax_cents: taxCents,
      items_price_cents: itemsPrice,
      subs_price_cents: subsPrice,
      cost_lines_price_cents: costLinesPrice.toCents(),
      decision_cents: decisions.toCents(),
      price_cents: price,
      raw_cost_cents: rawCost,
      margin_cents: margin,
      margin_pct: price === 0 ? '0' : Rat.of(BigInt(margin), BigInt(price)).mul(HUNDRED).toDecimal(1),
      unverified_items: outItems.filter((i) => i.unverified.any).length,
      overrides: outItems.reduce((n, i) => n + i.overrides_count, 0),
      warnings: warnings.length + lineWarnings + outItems.reduce((n, i) => n + i.warnings.length + i.rule_warnings.length, 0),
    },
    warnings,
  };
}
